import React, { useState, useEffect, useRef } from "react";
import { Form, Input, Button, Upload, Select, message } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { binaryContentsHooks as cHooks } from "../hooks";

const { Option } = Select;

const CreateContentPage = () => {
  const [form] = Form.useForm();
  const [thumbnailBase64, setThumbnailBase64] = useState(null);
  const [backgroundBase64, setBackgroundBase64] = useState(null);
  const [groupList, setGroupList] = useState([]);
  const [authorList, setAuthorList] = useState([]);
  const [htmlContent, setHtmlContent] = useState("");
  const [uploadedImageUrl, setUploadedImageUrl] = useState("");
  const [isPreview, setIsPreview] = useState(false);
  const quillRef = useRef(null);

  const fetchGroups = async () => {
    try {
      const res = await cHooks.contentsGroupList();
      if (res.data) setGroupList(res.data.data);
    } catch (error) {
      message.error("그룹 리스트 불러오기 실패!");
    }
  };

  const authorGroups = async () => {
    try {
      const res = await cHooks.contentsAuthorList();
      if (res.data) setAuthorList(res.data.data);
    } catch (error) {
      message.error("작성자 리스트 불러오기 실패!");
    }
  };

  useEffect(() => {
    fetchGroups();
    authorGroups();
  }, []);

  const handleBase64Upload = (file, setter) => {
    const reader = new FileReader();
    reader.onload = (e) => setter(e.target.result);
    reader.readAsDataURL(file);
    return false;
  };

  const handleImageUploadAndGetUrl = async (file) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64Image = e.target.result.split(",")[1];
      try {
        const res = await cHooks.contentsImageUpload({ base64Image });
        if (res.data?.url) {
          setUploadedImageUrl(res.data.url);
          message.success("이미지 URL 반환 성공!");
        } else {
          throw new Error("URL 반환 실패");
        }
      } catch (error) {
        message.error("이미지 업로드 실패!");
      }
    };
    reader.readAsDataURL(file);
    return false;
  };

  const handleInsertImage = () => {
    if (uploadedImageUrl) {
      const quill = quillRef.current.getEditor();
      const currentPosition = quill.getSelection()?.index || quill.getLength();
      quill.insertEmbed(currentPosition, "image", uploadedImageUrl);
      setUploadedImageUrl("");
      message.success("이미지가 삽입되었습니다!");
    } else {
      message.error("유효한 이미지 URL이 없습니다!");
    }
  };

  const handleSubmit = async (values) => {
    try {
      const payload = {
        ...values,
        content_thumbnail: thumbnailBase64?.split(",")[1],
        content_background_image: backgroundBase64?.split(",")[1],
        content_description: btoa(unescape(encodeURIComponent(htmlContent))),
      };
      const res = await cHooks.contentsCreate({
          authorId: payload.author_id,
          contentTitle: payload.content_title,
          contentDescription: payload.content_description,
          contentThumbnail: payload.content_thumbnail,
          contentBackgroundImage: payload.content_background_image,
          contentType: payload.content_type, // ✅ 정확히 맞춰 전달
        });
      if (res.IsError) throw new Error();
      message.success("컨텐츠가 성공적으로 생성되었습니다!");
      form.resetFields();
      
    } catch (error) {
      message.error("컨텐츠 업로드 실패");
    }
  };

  return (
    <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "20px" }}>
      <h2>컨텐츠 생성 페이지</h2>
      <Form layout="vertical" form={form} onFinish={handleSubmit}>
        <Form.Item
          name="content_title"
          label="제목"
          rules={[{ required: true }]}
        >
          <Input.TextArea rows={2} />
        </Form.Item>

        {/* 컨텐츠 타입 드롭다운 */}
        <Form.Item
          name="content_type"
          label="컨텐츠 타입"
          rules={[{ required: true, message: "컨텐츠 타입을 선택해주세요" }]}
        >
          <Select placeholder="컨텐츠 타입 선택">
            <Option value="고위험 임신">고위험 임신</Option>
            <Option value="궁금해요">궁금해요</Option>
          </Select>
        </Form.Item>

        <Form.Item label="썸네일">
          <Upload
            beforeUpload={(file) =>
              handleBase64Upload(file, setThumbnailBase64)
            }
            showUploadList={false}
          >
            <Button icon={<UploadOutlined />}>썸네일 업로드</Button>
          </Upload>
          {thumbnailBase64 && (
            <img
              src={thumbnailBase64}
              alt="썸네일"
              style={{ width: "150px", marginTop: "10px" }}
            />
          )}
        </Form.Item>

        <Form.Item label="백그라운드 이미지">
          <Upload
            beforeUpload={(file) =>
              handleBase64Upload(file, setBackgroundBase64)
            }
            showUploadList={false}
          >
            <Button icon={<UploadOutlined />}>백그라운드 이미지 업로드</Button>
          </Upload>
          {backgroundBase64 && (
            <img
              src={backgroundBase64}
              alt="배경 이미지"
              style={{ width: "150px", marginTop: "10px" }}
            />
          )}
        </Form.Item>

        <Form.Item label="이미지 업로드 및 URL 반환">
          <Upload
            beforeUpload={handleImageUploadAndGetUrl}
            showUploadList={false}
          >
            <Button icon={<UploadOutlined />}>이미지 업로드</Button>
          </Upload>
          <Input
            placeholder="반환된 이미지 URL"
            value={uploadedImageUrl}
            onChange={(e) => setUploadedImageUrl(e.target.value)}
            style={{ marginTop: "10px" }}
          />
          <Button
            type="dashed"
            onClick={handleInsertImage}
            disabled={!uploadedImageUrl}
            style={{ marginTop: "10px" }}
          >
            에디터에 이미지 삽입
          </Button>
        </Form.Item>

        <Form.Item label="내용">
          <Button
            type="default"
            onClick={() => setIsPreview(!isPreview)}
            style={{ marginBottom: "10px" }}
          >
            {isPreview ? "에디터로 돌아가기" : "미리보기"}
          </Button>
          {!isPreview ? (
            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={htmlContent}
              onChange={setHtmlContent}
              modules={{
                toolbar: [
                  [{ header: [1, 2, 3, 4, 5, false] }],
                  [{ size: ['small', false, 'large', 'huge'] }],
                  ["bold", "italic", "underline", "strike"],
                  [{ color: [] }, { background: [] }],
                  [{ align: [] }],
                  [{ list: "ordered" }, { list: "bullet" }],
                  ["link"],
                  ["clean"],
                ],
              }}
              style={{ height: "300px", marginBottom: "50px", width: "360px" }}
            />
          ) : (
            <div
              dangerouslySetInnerHTML={{ __html: htmlContent }}
              style={{ border: "1px solid #ddd", padding: "10px" }}
            />
          )}
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit">
            생성하기
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default CreateContentPage;
